import { UNSUPPORTED_FILE_TYPE_MESSAGE, isAllowedName } from '@/lib/tabularFile';
import { useEffect, useRef, useState } from 'react';

export interface UseFileDropUploadOptions {
  enabled?: boolean;
  maxAttachments: number;
  currentCount: number;
  /** 서버가 내려준 허용 확장자(점 없는 소문자, DRM 반영). 미지정 시 전체 첨부 형식 fallback. */
  allowedExts?: readonly string[];
  /**
   * 드롭 시점에는 업로드하지 않고 File 객체를 그대로 부모에 전달한다. 실제 업로드는 사용자가
   * 메시지를 전송하는 시점에 부모가 일괄 수행한다.
   */
  onFilesDropped: (files: File[]) => void;
  onError?: (message: string) => void;
}

function isFileDrag(e: DragEvent): boolean {
  if (!e.dataTransfer) return false;
  const types = e.dataTransfer.types;
  for (let i = 0; i < types.length; i++) {
    const t = types[i];
    if (t === 'Files' || t === 'application/x-moz-file') return true;
  }
  return false;
}

/**
 * ChatGPT / Claude 스타일 윈도우 레벨 파일 드롭 훅. 드래그가 시작되면 화면 전체에 오버레이를 띄워
 * 어느 지점에 떨어뜨려도 첨부(엑셀·CSV·문서)되도록 한다. Session 화면과 새 대화 index 화면 양쪽에서 쓴다.
 *
 * - `dragenter`/`dragleave` 는 자식 엘리먼트 경계에서도 발생해 깜빡임을 유발하므로 counter 로 상쇄.
 * - `dragover` 는 무조건 preventDefault 해야 브라우저 기본동작 (파일 새 탭 열기) 을 막을 수 있다.
 */
export function useFileDropUpload({
  enabled = true,
  maxAttachments,
  currentCount,
  allowedExts,
  onFilesDropped,
  onError,
}: UseFileDropUploadOptions): { isDragging: boolean } {
  const [isDragging, setIsDragging] = useState(false);
  const counterRef = useRef(0);

  // 최신 props 를 listener 에서 읽기 위한 ref (listener 를 매번 재등록하지 않음).
  const stateRef = useRef({ maxAttachments, currentCount, allowedExts, onFilesDropped, onError });
  stateRef.current = { maxAttachments, currentCount, allowedExts, onFilesDropped, onError };

  useEffect(() => {
    if (!enabled) return;

    const onDragEnter = (e: DragEvent): void => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counterRef.current += 1;
      setIsDragging(true);
    };
    const onDragOver = (e: DragEvent): void => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent): void => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counterRef.current = Math.max(0, counterRef.current - 1);
      if (counterRef.current === 0) setIsDragging(false);
    };
    const onDrop = (e: DragEvent): void => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counterRef.current = 0;
      setIsDragging(false);

      const state = stateRef.current;
      const all = Array.from(e.dataTransfer?.files ?? []);
      if (all.length === 0) return;
      const accepted = all.filter((f) => isAllowedName(f.name, state.allowedExts));
      if (accepted.length === 0) {
        state.onError?.(UNSUPPORTED_FILE_TYPE_MESSAGE);
        return;
      }
      const slots = state.maxAttachments - state.currentCount;
      if (slots <= 0) {
        state.onError?.(`최대 ${state.maxAttachments}개까지만 첨부할 수 있습니다.`);
        return;
      }
      const toAttach = accepted.slice(0, slots);
      if (accepted.length > slots) {
        state.onError?.(
          `최대 ${state.maxAttachments}개 한도 초과 — ${toAttach.length}개만 첨부합니다.`,
        );
      }
      state.onFilesDropped(toAttach);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled]);

  return { isDragging };
}
